import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import {
  getDepositDeclaration,
  getWithdrawalDeclaration,
  refreshUserWallet,
  submitDepositDeclaration,
  submitWithdrawalDeclaration,
} from "../../../api/services/depositDeclarationService";
import { formatDepHistoryDate } from "./depositHistoryShared";
import DepositHistory from "./DepositHistory";
import WithdrawHistory from "./WithdrawHistory";
import {
  DEPOSIT_HISTORY_PATH,
  DEPOSIT_HISTORY_REFRESH,
  USER_WALLET_REFRESH,
  WALLET_DEPOSIT_UPDATED,
  WALLET_WITHDRAWAL_UPDATED,
  WITHDRAW_HISTORY_PATH,
  WITHDRAW_HISTORY_REFRESH,
  WITHDRAW_AWAITING_PATH,
  buildDeclarationBody,
  classifyDeclarationDetail,
  classifyDeclarationSubmit,
  classifyWithdrawalDetail,
  countrySelectOptions,
  localDeclarationError,
  optionLabel,
} from "./depositDeclarationModel";
import "./depositDeclaration.css";

const EMPTY_FORM = {
  senderIsSelf: null,
  sourceType: "",
  senderType: "",
  firstName: "",
  lastName: "",
  companyName: "",
  country: "",
  exchangeName: "",
  confirmed: false,
};

const LAST_STEP = 2;

function textOrDash(value) {
  if (value == null || value === "") return "—";
  return String(value);
}

function choiceLabel(mode, field, opt) {
  const value = opt?.value;
  if (mode === "withdrawal" && field === "sender") {
    if (value === true) return "I am sending this withdrawal";
    if (value === false) return "Someone else is the sender";
  }
  if (opt?.label != null && String(opt.label).trim()) return String(opt.label);
  return "";
}

function stepReady(step, state) {
  if (step === 0) return state.senderIsSelf === true || state.senderIsSelf === false;
  if (step === 1) {
    if (state.senderIsSelf === false && state.senderType !== "PERSONAL" && state.senderType !== "COMPANY") {
      return false;
    }
    const personal = state.senderIsSelf === true || state.senderType === "PERSONAL";
    if (personal && (!String(state.firstName || "").trim() || !String(state.lastName || "").trim())) return false;
    if (state.senderIsSelf === false && state.senderType === "COMPANY" && !String(state.companyName || "").trim()) {
      return false;
    }
    return Boolean(String(state.country || "").trim());
  }
  if (state.sourceType !== "PRIVATE_WALLET" && state.sourceType !== "EXCHANGE") return false;
  if (state.sourceType === "EXCHANGE" && !String(state.exchangeName || "").trim()) return false;
  return state.confirmed === true;
}

function Choice({ name, checked, label, onChange }) {
  return (
    <label className={`dd-modalChoice${checked ? " is-on" : ""}`}>
      <input type="radio" name={name} checked={checked} onChange={onChange} />
      <span className="dd-modalRadio" aria-hidden="true" />
      <span>{label}</span>
    </label>
  );
}

function Summary({ deposit, form, mode }) {
  const declaration = deposit?.declaration || {};
  const isWithdrawal = mode === "withdrawal";
  const personal = declaration.sender_type !== "COMPANY";
  const name = personal
    ? [declaration.first_name, declaration.last_name].filter(Boolean).join(" ")
    : declaration.company_name;
  const senderOpt = (form?.sender_is_self || []).find((opt) => opt?.value === declaration.sender_is_self);
  const typeOpt = (form?.sender_type || []).find((opt) => opt?.value === declaration.sender_type);
  const sourceOpt = (form?.source_type || []).find((opt) => opt?.value === declaration.source_type);
  const senderLabel = choiceLabel(mode, "sender", senderOpt || { value: declaration.sender_is_self })
    || optionLabel(form?.sender_is_self, declaration.sender_is_self);
  const typeLabel = choiceLabel(mode, "type", typeOpt || { value: declaration.sender_type })
    || optionLabel(form?.sender_type, declaration.sender_type);
  const sourceLabel = choiceLabel(mode, "source", sourceOpt || { value: declaration.source_type })
    || optionLabel(form?.source_type, declaration.source_type);
  const rows = [
    [isWithdrawal ? "Who is sending this withdrawal?" : "Who sent this deposit?", senderLabel],
    declaration.sender_is_self === false ? ["Originator type", typeLabel] : null,
    name ? ["Name", name] : null,
    declaration.country ? ["Country of residence", declaration.country] : null,
    ["Address type", sourceLabel],
    declaration.exchange_name ? ["Exchange name", declaration.exchange_name] : null,
    deposit?.declaration_submitted_at
      ? ["Submitted", formatDepHistoryDate(deposit.declaration_submitted_at)]
      : null,
  ].filter((row) => row && row[1]);

  return (
    <div className="dd-modalSummary" aria-label="Declaration summary">
      {rows.map(([label, value]) => (
        <div className="dd-modalSummaryRow" key={label}>
          <span>{label}</span>
          <span>{value}</span>
        </div>
      ))}
      {declaration.confirmed === true ? (
        <p className="dd-modalConfirmNote">I confirm this information is true and complete.</p>
      ) : null}
    </div>
  );
}

export default function DepositDeclaration({ kind = "deposit" }) {
  const isWithdrawal = kind === "withdrawal";
  const { depositId, withdrawalId } = useParams();
  const recordId = isWithdrawal ? withdrawalId : depositId;
  const navigate = useNavigate();
  const countries = useMemo(() => countrySelectOptions(), []);
  const [phase, setPhase] = useState("loading");
  const [payload, setPayload] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [formState, setFormState] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [retryCredit, setRetryCredit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);

  const historyPath = isWithdrawal ? WITHDRAW_HISTORY_PATH : DEPOSIT_HISTORY_PATH;

  const leave = useCallback(() => {
    navigate(historyPath, { replace: true });
  }, [navigate, historyPath]);

  const openAwaitingApproval = useCallback(() => {
    navigate(WITHDRAW_AWAITING_PATH, { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    const id = String(recordId || "").trim();
    if (!id) {
      leave();
      return;
    }
    setPhase("loading");
    setLoadError("");
    try {
      const res = isWithdrawal ? await getWithdrawalDeclaration(id) : await getDepositDeclaration(id);
      const classified = isWithdrawal ? classifyWithdrawalDetail(res) : classifyDeclarationDetail(res);
      if (classified.kind === "leave") {
        leave();
        return;
      }
      if (classified.kind !== "ok") {
        setPhase("error");
        setLoadError(classified.message);
        return;
      }
      const card = isWithdrawal ? classified.data.withdrawal : classified.data.deposit;
      const status = String(card?.status || "").toUpperCase();
      const declarationStatus = String(card?.declaration_status || "").toUpperCase();
      setPayload(classified.data);
      if (isWithdrawal) {
        if (status === "AWAITING_DECLARATION") {
          setPhase("form");
          return;
        }
        if (status === "PENDING" && declarationStatus === "SUBMITTED") {
          setPhase("summary");
          return;
        }
        setPhase("other");
        return;
      }
      if (status === "COMPLETED") {
        setPhase("summary");
        return;
      }
      if (status === "AWAITING_DECLARATION") {
        setPhase("form");
        return;
      }
      setPhase("other");
    } catch {
      setPhase("error");
      setLoadError(isWithdrawal ? "Could not load this withdrawal." : "Could not load this deposit.");
    }
  }, [recordId, leave, isWithdrawal]);

  useEffect(() => {
    setFormState(EMPTY_FORM);
    setFormError("");
    setRetryCredit(false);
    setStep(0);
    void load();
  }, [load]);

  useEffect(() => {
    const scroller = document.querySelector(".dd-modalScroll");
    if (scroller) scroller.scrollTop = 0;
  }, [step]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => {
      if (event.key === "Escape") leave();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [leave]);

  useEffect(() => {
    if (isWithdrawal) {
      const onUpdated = (event) => {
        const payloadEvent = event?.detail;
        if (!payloadEvent) return;
        const id = payloadEvent.withdrawal_id || payloadEvent.row?._id || payloadEvent.row?.withdrawal_id;
        if (String(id || "") !== String(recordId || "")) return;
        const status = String(payloadEvent.status || payloadEvent.row?.status || "").toUpperCase();
        const declarationStatus = String(payloadEvent.row?.declaration_status || "").toUpperCase();
        if (status === "PENDING" || declarationStatus === "SUBMITTED") openAwaitingApproval();
      };
      window.addEventListener(WALLET_WITHDRAWAL_UPDATED, onUpdated);
      return () => window.removeEventListener(WALLET_WITHDRAWAL_UPDATED, onUpdated);
    }
    const onUpdated = (event) => {
      const payloadEvent = event?.detail;
      if (!payloadEvent || payloadEvent.type !== "deposit") return;
      if (String(payloadEvent.deposit_id || "") !== String(recordId || "")) return;
      if (payloadEvent.status === "COMPLETED") leave();
    };
    window.addEventListener(WALLET_DEPOSIT_UPDATED, onUpdated);
    return () => window.removeEventListener(WALLET_DEPOSIT_UPDATED, onUpdated);
  }, [isWithdrawal, recordId, leave, openAwaitingApproval]);

  const deposit = (isWithdrawal ? payload?.withdrawal : payload?.deposit) || null;
  const form = payload?.form || {};
  const prefill = payload?.prefill || {};
  const showPersonalName = formState.senderIsSelf === true || formState.senderType === "PERSONAL";
  const showCompany = formState.senderIsSelf === false && formState.senderType === "COMPANY";
  const showSenderType = formState.senderIsSelf === false;
  const showExchange = formState.sourceType === "EXCHANGE";
  const canAdvance = stepReady(step, formState);

  const chooseSender = (value) => {
    setFormError("");
    setRetryCredit(false);
    setFormState((prev) => {
      if (value === true) {
        return {
          ...prev,
          senderIsSelf: true,
          senderType: "",
          firstName: prefill.first_name || "",
          lastName: prefill.last_name || "",
          companyName: "",
        };
      }
      return {
        ...prev,
        senderIsSelf: false,
        senderType: "",
        firstName: "",
        lastName: "",
        companyName: "",
      };
    });
  };

  const finishSuccess = async (message) => {
    try {
      await refreshUserWallet();
    } catch {
      /* The wallet screens refetch on the event below. Funds stay locked after a withdrawal declaration. */
    }
    window.dispatchEvent(new CustomEvent(USER_WALLET_REFRESH));
    if (isWithdrawal) {
      window.dispatchEvent(new CustomEvent(WITHDRAW_HISTORY_REFRESH));
      openAwaitingApproval();
      return;
    }
    if (message) alertSuccessMessage(message);
    window.dispatchEvent(new CustomEvent(DEPOSIT_HISTORY_REFRESH));
    leave();
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submitting || step < LAST_STEP) return;
    const message = localDeclarationError(formState);
    if (message) {
      setRetryCredit(false);
      setFormError(message);
      return;
    }
    setSubmitting(true);
    setFormError("");
    setRetryCredit(false);
    try {
      const body = buildDeclarationBody(formState);
      const res = isWithdrawal
        ? await submitWithdrawalDeclaration(recordId, body)
        : await submitDepositDeclaration(recordId, body);
      const classified = classifyDeclarationSubmit(res);
      if (classified.kind === "ok") {
        await finishSuccess(classified.message);
        return;
      }
      if (classified.kind === "leave") {
        window.dispatchEvent(
          new CustomEvent(isWithdrawal ? WITHDRAW_HISTORY_REFRESH : DEPOSIT_HISTORY_REFRESH)
        );
        leave();
        return;
      }
      if (classified.kind === "invalid") {
        setFormError(classified.message);
        return;
      }
      setRetryCredit(true);
    } catch {
      setRetryCredit(true);
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    if (!canAdvance || step >= LAST_STEP) return;
    setFormError("");
    setStep((current) => current + 1);
  };

  const modal = (
    <div className="dd-modalRoot">
      <div className="dd-modal" role="dialog" aria-modal="true" aria-labelledby="dd-modal-title">
        <div className="dd-modalHead">
          <h1 id="dd-modal-title">Address Confirmation</h1>
          <button type="button" className="dd-modalClose" aria-label="Close" onClick={leave}>
            ×
          </button>
        </div>

        {phase === "loading" ? <p className="dd-modalHint" role="status">Loading…</p> : null}

        {phase === "error" ? (
          <div role="alert">
            <p className="dd-formError">{loadError}</p>
            <div className="dd-modalActions">
              <button type="button" className="dd-modalCancel" onClick={leave}>Cancel</button>
              <button type="button" className="dd-modalNext" onClick={() => void load()}>Try again</button>
            </div>
          </div>
        ) : null}

        {deposit && phase !== "loading" && phase !== "error" ? (
          <p className="dd-modalFacts">
            <strong>
              {textOrDash(deposit.amount)} {textOrDash(deposit.short_name)}
            </strong>
            <span>Network {textOrDash(deposit.chain)}</span>
            <span>
              {isWithdrawal ? "To" : "From"}{" "}
              {textOrDash(isWithdrawal ? deposit.to_address : deposit.from_address)}
            </span>
            {isWithdrawal ? <span>Fee {textOrDash(deposit.fee)}</span> : null}
          </p>
        ) : null}

        {phase === "summary" ? (
          <>
            <Summary deposit={deposit} form={form} mode={isWithdrawal ? "withdrawal" : "deposit"} />
            <div className="dd-modalActions">
              <button type="button" className="dd-modalNext" onClick={leave}>Close</button>
            </div>
          </>
        ) : null}

        {phase === "other" ? (
          <>
            <p className="dd-modalHint">
              {isWithdrawal && String(deposit?.status || "").toUpperCase() === "PENDING"
                ? "Waiting for admin approval"
                : deposit?.status === "REJECTED"
                  ? "Blocked"
                  : deposit?.status === "PENDING"
                    ? "Pending"
                    : textOrDash(deposit?.status)}
            </p>
            <div className="dd-modalActions">
              <button
                type="button"
                className="dd-modalNext"
                onClick={
                  isWithdrawal && String(deposit?.status || "").toUpperCase() === "PENDING"
                    ? openAwaitingApproval
                    : leave
                }
              >
                Close
              </button>
            </div>
          </>
        ) : null}

        {phase === "form" ? (
          <form className="dd-modalForm" onSubmit={onSubmit} noValidate>
            <div className="dd-modalScroll">
            {step === 0 ? (
              <>
                <p className="dd-modalHint">
                  Please provide the details of the address owner (the person you are transacting with). These details will be used to comply with regulatory requirements when transacting with this address.
                </p>
                <p className="dd-modalHold">
                  {isWithdrawal
                    ? "Funds stay locked until you submit. An admin approves the withdrawal after that."
                    : "Your balance does not change until you submit."}
                </p>
                <h2 className="dd-modalSection">
                  {isWithdrawal ? "Who is sending this withdrawal?" : "Who sent this deposit?"}
                </h2>
                <div className="dd-modalOptions">
                  {(form.sender_is_self || []).map((opt) => (
                    <Choice
                      key={String(opt.value)}
                      name="sender_is_self"
                      checked={formState.senderIsSelf === opt.value}
                      label={choiceLabel(isWithdrawal ? "withdrawal" : "deposit", "sender", opt)}
                      onChange={() => chooseSender(opt.value)}
                    />
                  ))}
                </div>
              </>
            ) : null}

            {step === 1 ? (
              <>
                {showSenderType ? (
                  <>
                    <h2 className="dd-modalSection">Originator Type</h2>
                    <div className="dd-modalOptions">
                      {(form.sender_type || []).map((opt) => (
                        <Choice
                          key={String(opt.value)}
                          name="sender_type"
                          checked={formState.senderType === opt.value}
                          label={choiceLabel(isWithdrawal ? "withdrawal" : "deposit", "type", opt)}
                          onChange={() => {
                            setFormError("");
                            setFormState((prev) => ({
                              ...prev,
                              senderType: opt.value,
                              firstName: opt.value === "PERSONAL" ? prev.firstName : "",
                              lastName: opt.value === "PERSONAL" ? prev.lastName : "",
                              companyName: opt.value === "COMPANY" ? prev.companyName : "",
                            }));
                          }}
                        />
                      ))}
                    </div>
                  </>
                ) : null}

                {(showPersonalName || showCompany) ? <h2 className="dd-modalSection">Originator Details</h2> : null}

                {showPersonalName ? (
                  <>
                    <label className="dd-modalField">
                      <span>First name</span>
                      <input
                        value={formState.firstName}
                        placeholder="Enter here"
                        autoComplete="given-name"
                        onChange={(e) => setFormState((prev) => ({ ...prev, firstName: e.target.value }))}
                      />
                    </label>
                    <label className="dd-modalField">
                      <span>Last name</span>
                      <input
                        value={formState.lastName}
                        placeholder="Enter here"
                        autoComplete="family-name"
                        onChange={(e) => setFormState((prev) => ({ ...prev, lastName: e.target.value }))}
                      />
                    </label>
                  </>
                ) : null}

                {showCompany ? (
                  <label className="dd-modalField">
                    <span>Name (Full Name or Entity Name)</span>
                    <input
                      value={formState.companyName}
                      placeholder="Enter here"
                      onChange={(e) => setFormState((prev) => ({ ...prev, companyName: e.target.value }))}
                    />
                  </label>
                ) : null}

                {formState.senderIsSelf === true || formState.senderType ? (
                  <label className="dd-modalField">
                    <span>Country of Residence</span>
                    <select
                      value={formState.country}
                      onChange={(e) => setFormState((prev) => ({ ...prev, country: e.target.value }))}
                    >
                      <option value="">Select</option>
                      {countries.map((country) => (
                        <option key={country.value} value={country.value}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </>
            ) : null}

            {step === 2 ? (
              <>
                <h2 className="dd-modalSection">Address Type</h2>
                <div className="dd-modalOptions">
                  {(form.source_type || []).map((opt) => (
                    <Choice
                      key={String(opt.value)}
                      name="source_type"
                      checked={formState.sourceType === opt.value}
                      label={choiceLabel(isWithdrawal ? "withdrawal" : "deposit", "source", opt)}
                      onChange={() => {
                        setFormError("");
                        setFormState((prev) => ({
                          ...prev,
                          sourceType: opt.value,
                          exchangeName: opt.value === "EXCHANGE" ? prev.exchangeName : "",
                        }));
                      }}
                    />
                  ))}
                </div>
                {showExchange ? (
                  <label className="dd-modalField">
                    <span>Exchange name</span>
                    <input
                      value={formState.exchangeName}
                      placeholder="Enter here"
                      onChange={(e) => setFormState((prev) => ({ ...prev, exchangeName: e.target.value }))}
                    />
                  </label>
                ) : null}
                <label className="dd-modalCheck">
                  <input
                    type="checkbox"
                    checked={formState.confirmed}
                    onChange={(e) => setFormState((prev) => ({ ...prev, confirmed: e.target.checked }))}
                  />
                  <span>I confirm this information is true and complete.</span>
                </label>
              </>
            ) : null}
            </div>

            {formError ? (
              <p className="dd-formError" role="alert">
                {formError}
              </p>
            ) : null}
            {retryCredit ? (
              <p className="dd-formError" role="alert">
                {isWithdrawal
                  ? "Could not submit. This withdrawal is still waiting."
                  : "Credit failed. This deposit is still waiting."}
              </p>
            ) : null}

            <div className="dd-modalActions">
              <button type="button" className="dd-modalCancel" onClick={leave}>
                Cancel
              </button>
              {step < LAST_STEP ? (
                <button type="button" className="dd-modalNext" disabled={!canAdvance} onClick={goNext}>
                  Next
                </button>
              ) : (
                <button type="submit" className="dd-modalNext" disabled={!canAdvance || submitting}>
                  {submitting ? "Submitting…" : retryCredit ? "Try again" : "Submit"}
                </button>
              )}
            </div>
          </form>
        ) : null}
      </div>
    </div>
  );

  return (
    <>
      {isWithdrawal ? <WithdrawHistory /> : <DepositHistory />}
      {typeof document !== "undefined" ? createPortal(modal, document.body) : modal}
    </>
  );
}
